import type {
  TradeOption,
  TradeSelectionMeta,
} from "@simplybusiness/mobius-trade-selector";
import { useEffect, useState } from "react";
import type { AmbiguousTradeModalProps } from "./AmbiguousTradeModal";
import { findAmbiguousTradePair } from "./tradePairs";
import type { AmbiguousTradePair } from "./types";

const GENERAL_CONTRACTOR_POPUP_SELECTION_METHOD = "general_contractor_popup";

/** `TradeSelectionMeta` extended with an optional `selectionMethod` analytics override. */
export type TradeSelectionMetaWithMethod = TradeSelectionMeta & {
  selectionMethod?: string;
};

// `TradeSelectionMeta` satisfies this constraint — `selectionMethod` is optional so existing callers are unaffected.
type CommitTrade<TMeta extends TradeSelectionMetaWithMethod | undefined> = (
  trade: TradeOption,
  meta: TMeta,
) => void;

type UseAmbiguousTradeInterceptorResult<
  TMeta extends TradeSelectionMetaWithMethod | undefined,
> = {
  handleSelectTrade: (trade: TradeOption, meta: TMeta) => void;
  modalProps: AmbiguousTradeModalProps;
};

const withSelectionMethod = (
  meta: TradeSelectionMeta | undefined,
  selectionMethod: string,
): TradeSelectionMetaWithMethod | undefined =>
  meta !== undefined ? { ...meta, selectionMethod } : undefined;

export const useAmbiguousTradeInterceptor = <
  TMeta extends TradeSelectionMetaWithMethod | undefined =
    | TradeSelectionMetaWithMethod
    | undefined,
>(
  commitTrade: CommitTrade<TMeta>,
  onClearTrade?: () => void,
): UseAmbiguousTradeInterceptorResult<TMeta> => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingTrade, setPendingTrade] = useState<{
    trade: TradeOption;
    meta: TMeta;
    tradePair: AmbiguousTradePair;
  } | null>(null);

  // Clear pending trade when modal is dismissed
  useEffect(() => {
    if (!isOpen) {
      setPendingTrade(null);
    }
  }, [isOpen]);

  const handleSelectTrade = (trade: TradeOption, meta: TMeta) => {
    const tradePair = findAmbiguousTradePair(trade.value);
    if (tradePair) {
      // Pre-commit with general_contractor_popup selectionMethod so the event fires when the modal opens.
      commitTrade(
        trade,
        withSelectionMethod(
          meta,
          GENERAL_CONTRACTOR_POPUP_SELECTION_METHOD,
        ) as TMeta,
      );
      setPendingTrade({ trade, meta, tradePair });
      setIsOpen(true);
      return;
    }
    commitTrade(trade, meta);
  };

  const handleConfirmTrade = (confirmedTrade?: string) => {
    if (confirmedTrade && pendingTrade) {
      if (confirmedTrade !== pendingTrade.trade.value) {
        commitTrade(
          {
            value: confirmedTrade,
            label: confirmedTrade,
            vertical: pendingTrade.trade.vertical,
            verticalsWithoutRedirect:
              pendingTrade.trade.verticalsWithoutRedirect,
            definition: pendingTrade.tradePair.alternativeTradeDefinition,
          },
          withSelectionMethod(
            pendingTrade.meta,
            GENERAL_CONTRACTOR_POPUP_SELECTION_METHOD,
          ) as TMeta,
        );
      }
    } else {
      // "Select another trade" — clear the pre-committed trade so the selector reappears.
      onClearTrade?.();
    }
    setIsOpen(false);
  };

  return {
    handleSelectTrade,
    modalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      confirmTrade: handleConfirmTrade,
      tradePair: pendingTrade?.tradePair,
    },
  };
};
